/**
 * PlayPauseIcon (web) — the transport center button's animated play/pause
 * glyph, driven by the attached Lottie morph (play triangle at frame 0 →
 * pause bars at frame 36 → back to play at frame 67):
 *
 *   play→pause segment: frames 0 → 36
 *   pause→play segment: frames 36 → 67
 *
 * Fully imperative (forwardRef): `sync(paused)` snaps to the state's icon and
 * `animateTo(paused)` morphs to the state the user just switched the video TO
 * (YouTube-style animated button). Commands issued before the DotLottie
 * instance finishes loading are queued and applied on load. Reduced-motion
 * users get an instant snap instead of the morph.
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react'
import {View} from 'react-native'
import {useReducedMotion} from 'react-native-reanimated'
import {DotLottieReact} from '@lottiefiles/dotlottie-react'
import  {type DotLottie} from '@lottiefiles/dotlottie-web'

import playPauseButtonData from '../../../../assets/capsule/lottie/play-pause-button.json'

const TOTAL_FRAMES = 67
const FRAME_PAUSE = 36

export type PlayPauseIconHandle = {
  /** Snap to the icon of the given paused state (no animation). */
  sync: (paused: boolean) => void
  /** Morph to the icon of the given paused state (animated). */
  animateTo: (paused: boolean) => void
}

export const PlayPauseIcon = forwardRef<
  PlayPauseIconHandle,
  {size?: number}
>(function PlayPauseIcon({size = 40}, ref) {
  const dotLottieRef = useRef<DotLottie | null>(null)
  const pendingRef = useRef<(() => void) | null>(null)

  const reducedMotion = useReducedMotion()
  const reducedMotionRef = useRef(false)
  useEffect(() => {
    reducedMotionRef.current = reducedMotion
  }, [reducedMotion])

  const applyPending = useCallback(() => {
    const inst = dotLottieRef.current
    if (!inst || !inst.isLoaded) return
    const pending = pendingRef.current
    if (pending) {
      pendingRef.current = null
      pending()
    }
  }, [])

  // Commands before load are queued; the load listener flushes them.
  const queueOrRun = useCallback(
    (fn: () => void) => {
      pendingRef.current = fn
      applyPending()
    },
    [applyPending],
  )

  const onDotLottieRef = useCallback(
    (instance: DotLottie | null) => {
      dotLottieRef.current = instance
      if (instance) {
        if (instance.isLoaded) applyPending()
        else instance.addEventListener('load', applyPending)
      }
    },
    [applyPending],
  )

  useEffect(
    () => () => {
      const inst = dotLottieRef.current
      if (inst) inst.removeEventListener('load', applyPending)
    },
    [applyPending],
  )

  useImperativeHandle(ref, () => ({
    sync: (paused: boolean) => {
      const frame = paused ? 0 : FRAME_PAUSE
      queueOrRun(() => dotLottieRef.current?.setFrame(frame))
    },
    animateTo: (paused: boolean) => {
      if (reducedMotionRef.current) {
        const frame = paused ? 0 : FRAME_PAUSE
        queueOrRun(() => dotLottieRef.current?.setFrame(frame))
        return
      }
      queueOrRun(() => {
        const inst = dotLottieRef.current
        if (!inst) return
        if (paused) {
          // video → paused: morph pause bars (36) back into the play triangle.
          inst.setSegment(FRAME_PAUSE, TOTAL_FRAMES)
        } else {
          // video → playing: morph the play triangle (0) into the pause bars.
          inst.setSegment(0, FRAME_PAUSE)
        }
        inst.play()
      })
    },
  }))

  return (
    // The canvas must never intercept pointer events meant for the transport
    // button that hosts this icon.
    <View
      pointerEvents="none"
      style={{width: size, height: size}}
      collapsable={false}>
      {/* @ts-ignore data is web-only — plain Lottie JSON object */}
      <DotLottieReact
        data={playPauseButtonData}
        autoplay={false}
        loop={false}
        dotLottieRefCallback={onDotLottieRef}
        style={{width: '100%', height: '100%'}}
      />
    </View>
  )
})